// src/pages/ConsentPage/ConsentPage.js
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../auth/AuthContext';
import useCaseHistoryEnabled from '../../hooks/useCaseHistoryEnabled';
import SignaturePad from './SignaturePad';
import {
    APP_VERSION, deviceFingerprint, getConsentForCase, getConsentText, saveConsent, markCopyDelivered,
    listPendingWithdrawals, requestWithdrawal,
} from './consentApi';
import { getCase } from '../IntakeHub/caseHistoryApi';
import './ConsentPage.css';

const SIGNER_TYPES = [
    { key: 'parent', hi: 'माता / पिता', en: 'Parent' },
    { key: 'legal_guardian', hi: 'कानूनी अभिभावक', en: 'Legal guardian' },
    { key: 'other', hi: 'अन्य', en: 'Other' },
];

const STEPS = 4;

export default function ConsentPage() {
    const { caseId } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const { enabled, loading: flagLoading } = useCaseHistoryEnabled();

    const [text, setText] = useState(null);
    const [caseInfo, setCaseInfo] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [existing, setExisting] = useState(null);
    const [pendingWithdrawal, setPendingWithdrawal] = useState(null);
    const [step, setStep] = useState(1);
    const [saving, setSaving] = useState(false);
    const [saved, setSaved] = useState(null);

    // Withdrawal, raised from here. The family tells the intake worker, not the
    // therapist, and the request is what stops recording — so waiting until a
    // therapist is free means recording a child whose family already said stop.
    const [withdrawing, setWithdrawing] = useState(false);
    const [withdrawReason, setWithdrawReason] = useState('');
    const [withdrawError, setWithdrawError] = useState('');

    // Held in memory until the final step: consent is a legal event, so it is
    // committed whole or not at all. There is no partial save on purpose.
    const [form, setForm] = useState({
        signer_type: 'parent',
        signer_name: '',
        signer_relationship: '',
        signer_authority_text: '',
        disclosures_ack: [],
        permissions: {
            audio_clinical: false, audio_research: false, video_clinical: false, video_research: false,
        },
        signature_method: 'drawn',
        guardian_treatment_signature: null,
        guardian_research_signature: null,
        examiner_signature: null,
        copy_channel: 'print',
    });

    const load = useCallback(() => {
        setLoading(true);
        Promise.all([
            getConsentText(),
            getConsentForCase(caseId),
            getCase(caseId),
            // A failure here must not block signing a consent, so it answers
            // with an empty list rather than rejecting the whole page.
            listPendingWithdrawals().catch(() => ({ data: [] })),
        ])
            .then(([textRes, consentRes, caseRes, pendingRes]) => {
                setText(textRes.data);
                setCaseInfo(caseRes.data);
                const live = consentRes.data.find((r) => r.is_live);
                // A consent awaiting a withdrawal decision is no longer live, so
                // without this the page would offer a blank form for a child
                // whose family has already asked to stop.
                const awaiting = consentRes.data.find((r) => r.status === 'withdrawal_pending');
                const request = (pendingRes.data || []).find(
                    (r) => r.consent_id === (live || awaiting || {}).id
                );
                setExisting(live || awaiting || null);
                setPendingWithdrawal(awaiting ? (request || { reason: null }) : null);
                setError('');
            })
            .catch((err) => setError(
                err.response?.status === 404
                    ? 'This case could not be found.'
                    : 'Could not load the consent form. Check your connection and try again.'
            ))
            .finally(() => setLoading(false));
    }, [caseId]);

    useEffect(() => { if (enabled) load(); }, [enabled, load]);

    if (flagLoading || loading) return <div className="cn-centre">Loading…</div>;
    if (!enabled) return <div className="cn-centre">Case History is not switched on for this installation yet.</div>;
    if (error) return (
        <div className="cn-centre">
            <div className="cn-note red" style={{ marginBottom: 16 }}>{error}</div>
            <button className="cn-btn back" onClick={() => navigate('/intake')}>Back to Intake Hub</button>
        </div>
    );

    const toggleDisclosure = (key) => setForm((f) => ({
        ...f,
        disclosures_ack: f.disclosures_ack.includes(key)
            ? f.disclosures_ack.filter((k) => k !== key)
            : [...f.disclosures_ack, key],
    }));

    const setPermission = (key, value) => setForm((f) => ({
        ...f, permissions: { ...f.permissions, [key]: value },
    }));

    const allDisclosuresTicked = text
        && text.treatment.disclosures.every((d) => form.disclosures_ack.includes(d.key));
    const signaturesDone = form.signature_method === 'paper_scanned'
        ? !!form.examiner_signature
        : !!form.guardian_treatment_signature && !!form.examiner_signature;

    const submit = async () => {
        setSaving(true);
        setError('');
        try {
            const { data } = await saveConsent(caseId, {
                signer_type: form.signer_type,
                signer_name: form.signer_name.trim(),
                signer_relationship: form.signer_relationship,
                signer_authority_text: form.signer_authority_text,
                text_version: text.version,
                disclosures_ack: form.disclosures_ack,
                permission_audio_clinical: form.permissions.audio_clinical,
                permission_audio_research: form.permissions.audio_research,
                permission_video_clinical: form.permissions.video_clinical,
                permission_video_research: form.permissions.video_research,
                signature_method: form.signature_method,
                guardian_treatment_signature: form.guardian_treatment_signature,
                guardian_research_signature: form.guardian_research_signature,
                examiner_signature: form.examiner_signature,
                device_fingerprint: deviceFingerprint(),
                app_version: APP_VERSION,
                copy_channel: form.copy_channel,
            });
            setSaved(data);
        } catch (err) {
            const detail = err.response?.data?.detail;
            setError(typeof detail === 'string' ? detail : 'Could not save the consent. Nothing was recorded — please try again.');
        } finally {
            setSaving(false);
        }
    };

    const sendWithdrawal = async () => {
        setSaving(true);
        setWithdrawError('');
        try {
            await requestWithdrawal(existing.id, withdrawReason.trim());
            setWithdrawing(false);
            setWithdrawReason('');
            await load();
        } catch (err) {
            const detail = err.response?.data?.detail;
            setWithdrawError(typeof detail === 'string' ? detail
                : 'Could not record the request. Nothing was changed — please try again.');
        } finally {
            setSaving(false);
        }
    };

    const finish = async () => {
        try {
            await markCopyDelivered(saved.id, form.copy_channel);
        } catch {
            // The consent itself is saved; delivery can be recorded again later.
        }
        navigate('/intake');
    };

    const header = (
        <div className="cn-top">
            <div className="cn-title">
                <span className="hi">सहमति</span> · Consent
                <small>{caseInfo?.child_name} · Episode {caseInfo?.episode_number}</small>
            </div>
            <div className="cn-dots">
                {[1, 2, 3, 4].map((i) => <i key={i} className={i <= step ? 'done' : ''} />)}
                <b>Step {step} of {STEPS}</b>
            </div>
        </div>
    );

    const examinerStrip = (
        <div className="cn-examiner">
            <span>Examiner: {user?.username}</span>
            <span>Consent text {text?.version} · हिन्दी + English</span>
        </div>
    );

    // ---- already signed -----------------------------------------------------
    if (existing && !saved) {
        return (
            <div className="cn-page">
                {header}
                <div className="cn-body">
                    {pendingWithdrawal ? (
                        <>
                            <div className="cn-note amber">
                                <b>यह परिवार अपनी सहमति वापस लेना चाहता है · This family has asked to withdraw.</b>
                                <div style={{ marginTop: 6 }}>
                                    Recording has already stopped for this child. Whether the records
                                    are deleted is a therapist&apos;s decision, and it does not appear
                                    on this screen.
                                </div>
                                {pendingWithdrawal.reason && (
                                    <div style={{ marginTop: 6 }}>
                                        Reason given: {pendingWithdrawal.reason}
                                        {pendingWithdrawal.requested_by_name
                                            && ` — raised by ${pendingWithdrawal.requested_by_name}`}
                                    </div>
                                )}
                            </div>
                            <div className="cn-note blue">
                                If the family changes its mind, tell the therapist: refusing the
                                request puts the consent back in force. Nothing has been deleted yet.
                            </div>
                        </>
                    ) : (
                        <>
                            <div className="cn-note green">
                                <b>This child already has a signed consent.</b> Signed {new Date(existing.signed_at).toLocaleDateString()} by {existing.signer_name}.
                                It stays in force until withdrawn.
                                {existing.research_expires_at && ` Research permission runs to ${new Date(existing.research_expires_at).toLocaleDateString()}.`}
                            </div>
                            <div className="cn-note blue">
                                A consent is never edited. To change what was agreed, the current one
                                must be withdrawn first.
                            </div>

                            {withdrawError && <div className="cn-note red">{withdrawError}</div>}

                            {withdrawing ? (
                                <div className="cn-field">
                                    <label>
                                        परिवार ने क्या कहा? · What did the family say?
                                    </label>
                                    <textarea
                                        rows="3"
                                        value={withdrawReason}
                                        onChange={(e) => setWithdrawReason(e.target.value)}
                                        placeholder="In their own words, as far as you can."
                                    />
                                    <div className="cn-note amber" style={{ marginTop: 10 }}>
                                        Sending this <b>stops recording for this child straight away</b>.
                                        It does not delete anything: a therapist decides that, and a
                                        second therapist has to agree.
                                    </div>
                                    <div className="cn-actions">
                                        <button
                                            className="cn-btn"
                                            disabled={saving || !withdrawReason.trim()}
                                            onClick={sendWithdrawal}
                                        >
                                            {saving ? 'Sending…' : 'Send to the therapist'}
                                        </button>
                                        <button
                                            className="cn-btn back"
                                            disabled={saving}
                                            onClick={() => { setWithdrawing(false); setWithdrawError(''); }}
                                        >
                                            Cancel
                                        </button>
                                    </div>
                                    {!withdrawReason.trim() && (
                                        <div className="cn-hint">
                                            A reason is required — it is what the therapist reads
                                            before deciding.
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <button className="cn-btn warn" onClick={() => setWithdrawing(true)}>
                                    परिवार सहमति वापस लेना चाहता है · Family wants to withdraw…
                                </button>
                            )}
                        </>
                    )}
                    <button className="cn-btn back" onClick={() => navigate('/intake')}>Back to Intake Hub</button>
                </div>
                {examinerStrip}
            </div>
        );
    }

    // ---- done ---------------------------------------------------------------
    if (saved) {
        return (
            <div className="cn-page">
                {header}
                <div className="cn-body">
                    <div className="cn-note green">
                        <b>Consent saved.</b> Signed by {saved.signer_name}. Consent to assess and treat stays in force until withdrawn.
                        {saved.research_expires_at && ` Research permission runs to ${new Date(saved.research_expires_at).toLocaleDateString()}.`}
                    </div>
                    <div className="cn-field">
                        <label>आपकी कॉपी · The guardian's copy</label>
                        <div className="cn-opts">
                            {[['print', 'प्रिंट', 'Print'], ['email', 'ईमेल', 'Email'], ['both', 'दोनों', 'Both']].map(([key, hi, en]) => (
                                <button
                                    key={key}
                                    type="button"
                                    className={`cn-opt ${form.copy_channel === key ? 'on' : ''}`}
                                    onClick={() => setForm({ ...form, copy_channel: key })}
                                >
                                    <span className="h">{hi}</span><span className="e">{en}</span>
                                </button>
                            ))}
                        </div>
                    </div>
                    <div className="cn-note amber">
                        There is no email sending yet, so the copy has to be printed or handed over by hand.
                        Record here how it was given.
                    </div>
                    <div className="cn-actions">
                        <span />
                        <button className="cn-btn" onClick={finish}>Copy given — finish</button>
                    </div>
                </div>
                {examinerStrip}
            </div>
        );
    }

    // ---- the four steps -----------------------------------------------------
    return (
        <div className="cn-page">
            {header}
            <div className="cn-body">
                {text?.is_draft && (
                    <div className={`cn-note ${text.can_sign ? 'amber' : 'red'}`}>
                        <b>This consent wording is still a draft.</b>{' '}
                        {text.can_sign
                            ? 'Signing is allowed here for testing only. Never use it with a real family.'
                            : 'It cannot be signed until a speech therapist and your legal reviewer approve it. A signature taken against unapproved wording cannot be repaired afterwards.'}
                    </div>
                )}
                {error && <div className="cn-note red">{error}</div>}

                {step === 1 && (
                    <>
                        <div className="cn-q">
                            <div className="hi">आप बच्चे के कौन हैं?</div>
                            <div className="en">Who is signing for the child?</div>
                        </div>
                        <div className="cn-opts">
                            {SIGNER_TYPES.map((s) => (
                                <button
                                    key={s.key}
                                    type="button"
                                    className={`cn-opt ${form.signer_type === s.key ? 'on' : ''}`}
                                    onClick={() => setForm({ ...form, signer_type: s.key })}
                                >
                                    <span className="h">{s.hi}</span><span className="e">{s.en}</span>
                                </button>
                            ))}
                        </div>
                        <div className="cn-grid2">
                            <div className="cn-field">
                                <label>पूरा नाम · Full name</label>
                                <input value={form.signer_name} onChange={(e) => setForm({ ...form, signer_name: e.target.value })} />
                            </div>
                            <div className="cn-field">
                                <label>रिश्ता · Relationship to the child</label>
                                <input value={form.signer_relationship} onChange={(e) => setForm({ ...form, signer_relationship: e.target.value })} />
                            </div>
                        </div>
                        <div className="cn-field">
                            <label>अधिकार का विवरण · Authority to act for the child</label>
                            <input value={form.signer_authority_text} onChange={(e) => setForm({ ...form, signer_authority_text: e.target.value })} />
                        </div>
                        <div className="cn-actions">
                            <button className="cn-btn back" onClick={() => navigate('/intake')}>Cancel</button>
                            <button className="cn-btn" disabled={!form.signer_name.trim()} onClick={() => setStep(2)}>
                                आगे · Continue
                            </button>
                        </div>
                    </>
                )}

                {step === 2 && (
                    <>
                        <div className="cn-readaloud">
                            <span className="k">Read aloud · पढ़कर सुनाएँ</span>
                            <div className="hi">{text.treatment.read_aloud.hi}</div>
                            <div style={{ marginTop: 6 }}>{text.treatment.read_aloud.en}</div>
                        </div>
                        <div className="cn-list">
                            {text.treatment.disclosures.map((d) => (
                                <button
                                    key={d.key}
                                    type="button"
                                    className={`cn-check ${form.disclosures_ack.includes(d.key) ? 'on' : ''}`}
                                    onClick={() => toggleDisclosure(d.key)}
                                    aria-pressed={form.disclosures_ack.includes(d.key)}
                                >
                                    <span className="box">{form.disclosures_ack.includes(d.key) ? '✓' : ''}</span>
                                    <span>
                                        <span className="h">{d.hi}</span>
                                        <span className="e" style={{ display: 'block' }}>{d.en}</span>
                                    </span>
                                </button>
                            ))}
                        </div>
                        {text.treatment.duration && (
                            <div className="cn-note blue">
                                <span className="hi">{text.treatment.duration.hi}</span>
                                <div style={{ marginTop: 4 }}>{text.treatment.duration.en}</div>
                            </div>
                        )}
                        {!allDisclosuresTicked && (
                            <div className="cn-note amber">
                                Each point is ticked only after the guardian says yes to it. An unticked point counts as not agreed.
                            </div>
                        )}
                        <div className="cn-actions">
                            <button className="cn-btn back" onClick={() => setStep(1)}>पीछे · Back</button>
                            <button className="cn-btn" disabled={!allDisclosuresTicked} onClick={() => setStep(3)}>
                                आगे · Continue
                            </button>
                        </div>
                    </>
                )}

                {step === 3 && (
                    <>
                        <div className="cn-note red" style={{ fontSize: '1rem' }}>
                            <b className="hi">{text.research.notice.hi}</b>
                            <div style={{ marginTop: 4 }}>{text.research.notice.en}</div>
                        </div>
                        <div className="cn-list">
                            {text.research.permissions.map((p) => (
                                <div key={p.key} className="cn-perm">
                                    <span>
                                        <span className="h hi" style={{ fontSize: '1.1rem', fontWeight: 600 }}>{p.hi}</span>
                                        <span className="e" style={{ display: 'block', fontSize: '0.86rem', color: '#667085' }}>{p.en}</span>
                                    </span>
                                    <span className="cn-yn">
                                        <button
                                            type="button"
                                            className={form.permissions[p.key] ? 'yes' : ''}
                                            onClick={() => setPermission(p.key, true)}
                                        >
                                            हाँ Yes
                                        </button>
                                        <button
                                            type="button"
                                            className={!form.permissions[p.key] ? 'no' : ''}
                                            onClick={() => setPermission(p.key, false)}
                                        >
                                            नहीं No
                                        </button>
                                    </span>
                                </div>
                            ))}
                        </div>
                        <div className="cn-note green">
                            <b className="hi">{text.research.anonymisation.hi}</b>
                            <div style={{ marginTop: 4 }}>{text.research.anonymisation.en}</div>
                        </div>
                        <div className="cn-note blue">
                            {text.research.statements.map((s) => (
                                <div key={s.key} style={{ marginBottom: 6 }}>
                                    <span className="hi">{s.hi}</span>
                                    <span style={{ display: 'block', fontSize: '0.86rem' }}>{s.en}</span>
                                </div>
                            ))}
                        </div>
                        {!form.permissions.audio_clinical && (
                            <div className="cn-note amber">
                                Without permission for audio, nothing can be recorded and the assessment cannot go ahead.
                                The research permissions are free to decline.
                            </div>
                        )}
                        <div className="cn-actions">
                            <button className="cn-btn back" onClick={() => setStep(2)}>पीछे · Back</button>
                            <button className="cn-btn" onClick={() => setStep(4)}>आगे · Continue</button>
                        </div>
                    </>
                )}

                {step === 4 && (
                    <>
                        <div className="cn-field">
                            <label>हस्ताक्षर कैसे · How is this being signed?</label>
                            <div className="cn-opts">
                                {text.signature.methods.map((m) => (
                                    <button
                                        key={m.key}
                                        type="button"
                                        className={`cn-opt ${form.signature_method === m.key ? 'on' : ''}`}
                                        onClick={() => setForm({ ...form, signature_method: m.key })}
                                    >
                                        <span className="h">{m.hi}</span><span className="e">{m.en}</span>
                                    </button>
                                ))}
                            </div>
                        </div>

                        {form.signature_method === 'paper_scanned' ? (
                            <div className="cn-note amber">
                                The signed paper form must be photographed and attached to the child's file.
                                The examiner still countersigns below.
                            </div>
                        ) : (
                            <>
                                <SignaturePad
                                    label={`${text.signature.guardian_prompt.hi} · Treatment consent`}
                                    onChange={(sig) => setForm((f) => ({ ...f, guardian_treatment_signature: sig }))}
                                />
                                <SignaturePad
                                    label={`${text.signature.guardian_prompt.hi} · Research permission`}
                                    onChange={(sig) => setForm((f) => ({ ...f, guardian_research_signature: sig }))}
                                />
                            </>
                        )}

                        <div className="cn-note blue">
                            <span className="hi">{text.signature.examiner_declaration.hi}</span>
                            <div style={{ marginTop: 4 }}>{text.signature.examiner_declaration.en}</div>
                        </div>
                        <SignaturePad
                            label={`Examiner countersignature · ${user?.username}`}
                            onChange={(sig) => setForm((f) => ({ ...f, examiner_signature: sig }))}
                        />

                        <div className="cn-actions">
                            <button className="cn-btn back" onClick={() => setStep(3)}>पीछे · Back</button>
                            <button
                                className="cn-btn"
                                disabled={!signaturesDone || saving || text?.can_sign === false}
                                title={text?.can_sign === false ? 'The consent wording has not been approved yet' : undefined}
                                onClick={submit}
                            >
                                {saving ? 'Saving…' : 'सहमति सेव करें · Save consent'}
                            </button>
                        </div>
                    </>
                )}
            </div>
            {examinerStrip}
        </div>
    );
}
